'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ArrowDown, ArrowRight } from 'lucide-react';
import { useTranslation } from '@/lib/locales';
interface DirectionCardProps {
  image?: string;
  fallbackGradient: string;
  title: React.ReactNode;
  subtitle: string;
  href?: string;
  linkLabel: string;
  linkIcon?: 'right' | 'down';
}
function DirectionCard(t0) {
  const $ = _c(25);
  const {
    image,
    fallbackGradient,
    title,
    subtitle,
    href,
    linkLabel,
    linkIcon: t1
  } = t0;
  const linkIcon = t1 === undefined ? "right" : t1;
  const Icon = linkIcon === "down" ? ArrowDown : ArrowRight;
  const t2 = `relative aspect-[4/3] overflow-hidden ${fallbackGradient}`;
  let t3;
  if ($[0] !== image) {
    t3 = image && <img src={image} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={_temp} />;
    $[0] = image;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== t2 || $[3] !== t3) {
    t4 = <div className={t2}>{t3}</div>;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== title) {
    t5 = <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">{title}</h3>;
    $[5] = title;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== subtitle) {
    t6 = <p className="text-gray-500 text-sm leading-relaxed mb-4">{subtitle}</p>;
    $[7] = subtitle;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = `h-4 w-4 transition-transform ${linkIcon === "down" ? "group-hover:translate-y-0.5" : "group-hover:translate-x-0.5"}`;
  let t8;
  if ($[9] !== Icon || $[10] !== t7) {
    t8 = <Icon className={t7} />;
    $[9] = Icon;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== linkLabel || $[13] !== t8) {
    t9 = <span className="mt-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 group-hover:underline">{linkLabel}{t8}</span>;
    $[12] = linkLabel;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t9) {
    t10 = <div className="p-6 flex flex-col items-center text-center flex-1">{t5}{t6}{t9}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t4) {
    t11 = <div className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 flex flex-col h-full cursor-pointer">{t4}{t10}</div>;
    $[19] = t10;
    $[20] = t4;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const content = t11;
  if (href) {
    let t12;
    if ($[22] !== content || $[23] !== href) {
      t12 = <Link href={href}>{content}</Link>;
      $[22] = content;
      $[23] = href;
      $[24] = t12;
    } else {
      t12 = $[24];
    }
    return t12;
  }
  return content;
}
function _temp(e) {
  (e.target as HTMLImageElement).style.display = "none";
}
export function DirectToRightPlace() {
  const $ = _c(26);
  const {
    t
  } = useTranslation();
  const scrollToContent = _temp2;
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white text-center mb-12">How do you want to use Sizzle today?</h2>;
    t1 = <p className="mx-auto mb-12 max-w-3xl text-center text-lg text-gray-300">Whether you're here to discover something new for yourself, grow a product, or help create the places and moments where discovery happens, we'll point you in the right direction.</p>;
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <DirectionCard fallbackGradient="bg-gradient-to-br from-orange-200 to-orange-400" image="https://plus.unsplash.com/premium_photo-1761846736887-49c35b17b1b6?w=600&h=450&fit=crop" href="/opportunities" title={<span>I love{" "}<span className="underline decoration-primary-500 decoration-3 underline-offset-2">discovering and trying new things</span></span>} subtitle="I want to discover products, find offers, attend tastings & experiences" linkLabel="Explore Offers and Experiences" linkIcon="right" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <span className="underline decoration-primary-500 decoration-3 underline-offset-2">make</span>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t("industry");
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t4) {
    t5 = <span>I{" "}{t3}{" "}{t4} products</span>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = t("industry");
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `I'm a brand, producer, grower, or manufacturer of ${t6} products`;
  let t8;
  if ($[10] !== t5 || $[11] !== t7) {
    t8 = <div onClick={scrollToContent}><DirectionCard fallbackGradient="bg-gradient-to-br from-purple-200 to-purple-400" image="https://plus.unsplash.com/premium_photo-1682144509812-4734c830b486?w=600&h=450&fit=crop" title={t5} subtitle={t7} linkLabel="Keep reading" linkIcon="down" /></div>;
    $[10] = t5;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <span className="underline decoration-primary-500 decoration-3 underline-offset-2">source or sell</span>;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== t) {
    t10 = t("industry");
    $[14] = t;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== t10) {
    t11 = <span>I{" "}{t9}{" "}{t10} products</span>;
    $[16] = t10;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== t) {
    t12 = t("industry");
    $[18] = t;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  const t13 = `I'm a buyer, retailer, importer, or distributor of ${t12} products`;
  let t14;
  if ($[20] !== t11 || $[21] !== t13) {
    t14 = <div onClick={scrollToContent}><DirectionCard fallbackGradient="bg-gradient-to-br from-blue-200 to-blue-400" image="https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=600&h=450&fit=crop" title={t11} subtitle={t13} linkLabel="Keep reading" linkIcon="down" /></div>;
    $[20] = t11;
    $[21] = t13;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] !== t14 || $[24] !== t8) {
    t15 = <section className="py-16 lg:py-24 bg-gradient-to-b from-gray-900 to-slate-900"><div className="container mx-auto px-4 sm:px-6 lg:px-8">{t0}{t1}<div className="grid grid-cols-1 sm:grid-cols-3 gap-8 max-w-5xl mx-auto">{t2}{t8}{t14}</div></div></section>;
    $[23] = t14;
    $[24] = t8;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  return t15;
}
function _temp2() {
  const el = document.getElementById("sizzle-for-section");
  el?.scrollIntoView({
    behavior: "smooth"
  });
}